import { Helmet } from 'react-helmet-async';
import OrganizationView from './components/OrganizationView';
import {
  PageTitleWrapper,
  PageHeader,
  SimpleDialog,
  CustomForm,
  showErrorMessage
} from 'src/shared/components/index';
import {
  Grid,
  Container,
  Box,
  Typography,
  Button,
  TextField,
  ToggleButtonGroup,
  ToggleButton
} from '@mui/material';
import { useEffect, useState, MouseEvent, FC } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  getOrganizationData,
  saveOrganization,
  setIsOrganizationListUpdated,
  updateOrganization,
  setIsError,
  getTenantUserOrganizationData,
  updateOrganizationPermission,
  getTenantPermissionDetails,
  selectOrganizationState
} from './services/organization.service';

import AddTwoToneIcon from '@mui/icons-material/AddTwoTone';
import { useTranslation } from 'react-i18next';
import { FormFieldType } from 'src/shared/components/form-field/service/formFieldInterface';
import {
  selectUserState,
  setselectedOrganization,
  setUserRole
} from 'src/auth/login/services/userSlice';
import * as ROUTES from '../../shared/constants/routes';
import { useNavigate } from 'react-router';
import {
  I18N,
  LIST_VIEW,
  ROLES,
  USER_TYPE
} from 'src/shared/constants/constants';
import GridViewSharpIcon from '@mui/icons-material/GridViewSharp';
import TableRowsTwoToneIcon from '@mui/icons-material/TableRowsTwoTone';

const Organization = () => {
  const navigate = useNavigate();
  const [open, setOpen] = useState<boolean>(false);
  const [isAdd, setIsAdd] = useState<boolean>(false);
  const { i18n, t } = useTranslation(I18N.ORGANIZATION);
  const dispatch = useDispatch<any>();
  const [selectedTab, setSelectedTab] = useState<string | null>(
    LIST_VIEW.TABLE_VIEW
  );
  const [organizationList, setOrganizationList] = useState([]);
  const {
    permissionData,
    organizationData,
    loadingPermissionDetails,
    loadingUpdatePermission,
    isOrganizationListUpdated,
    isError,
    errorMessage
  } = useSelector(selectOrganizationState);
  const { userInfo, loginUserRole, loginUserType, tenantUserRoles, userData } =
    useSelector(selectUserState);
  const [fields, setFields] = useState([] as FormFieldType[]);
  const [selectedOrganization, setSelectedOrganization] = useState({});
  const [inputInitialValues, setInputInitialValues] = useState({
    name: '',
    code: '',
    id: '',
    gstNo: '',
    phoneNo: '',
    isAssetSold: false,
    minBookingInterval: 3
  });
  const tenantNameField: FormFieldType[] = [{
    id: 'name',
    name: 'name',
    type: 'alphanumerics',
    label: 'Company Name',
    validations: {
      required: true,
      maxLength: 20
    }
    },
    {
    id: 'code',
    name: 'code',
    type: 'freetext',
    label: 'Organization code',
    validations: {
      required: true,
      maxLength: 20
    },
  },{
    id: 'gstNo',
    name: 'gstNo',
    type: 'freetext',
    label: 'GST No',
    validations: {
      maxLength: 20
    },
  },{
    id: 'mobileNo',
    name: 'phoneNo',
    type: 'freetext',
    label: 'Mobile No',
    validations: {
      maxLength: 10
    },
  },{
    id: 'isAssetSold',
    name: 'isAssetSold',
    type: 'switch',
    label: 'Sold Enabled',
  },{
    id: 'minBookingInterval',
    name: 'minBookingInterval',
    type: 'integer_only',
    label: 'Min Booking Interval',
    validations: {
      required: true,
      maxLength: 2,
      minLength: 1.
  }}];

  useEffect(() => {
    if (isError) {
      dispatch(setIsError(false));
      if (errorMessage !== '') {
        return showErrorMessage(errorMessage, {
          theme: 'colored',
          position: 'top-right',
          autoClose: 5000
        });
      }
    }
  }, [isError]);
  useEffect(() => {
    if (permissionData && !loadingPermissionDetails) {
      setInputInitialValues({
        ...inputInitialValues
      });
    }
  }, [loadingPermissionDetails]);
  useEffect(() => {
    if (!loadingUpdatePermission) {
      closeDialog();
    }
  }, [loadingUpdatePermission]);
  useEffect(() => {
    if (loginUserType === USER_TYPE.ADMIN_USER) {
      dispatch(setselectedOrganization({}));
      dispatch(getOrganizationData());
    } else {
      dispatch(setselectedOrganization({}));
      setUserRole('');
      dispatch(getTenantUserOrganizationData(userInfo.id));
    }
    setFields(tenantNameField);
  }, []);

  useEffect(() => {
    setOrganizationList(organizationData);
    if (
      loginUserType === USER_TYPE.TENANT_USER &&
      organizationData.length === 1
    ) {
      handleTenantNameClick(organizationData[0]);
    } else if (
      loginUserType === USER_TYPE.TENANT_USER &&
      organizationData.length === 1
    ) {
      dispatch(setUserRole(''));
    }
  }, [organizationData]);

  //methods
  const handleAddOrgButtonClick = () => {
    setFields(tenantNameField);
    setInputInitialValues({
      name: '',
      code: '',
      id: '',
      gstNo: '',
      phoneNo: '',
      isAssetSold: false,
      minBookingInterval: 3
    });
    setOpen(true);
    setIsAdd(true);
  };

  const handleFilterOrganization = (value: string) => {
    const filterOrganizationData = organizationData.filter(function (item) {
      return item.tenantName.toLowerCase().includes(value.toLowerCase());
    });
    setOrganizationList(filterOrganizationData);
  };

  const handleClose = (value) => {
    setOpen(false);
  };

  const closeDialog = () => {
    setOpen(false);
  };

  const handleAddModalCancel = () => {
    closeDialog();
  };

  const handleEditIconClick = (row) => {
    setSelectedOrganization(row);
    setIsAdd(false);
    setInputInitialValues({
      ...row,
      name: row.tenantName,
    });
    setOpen(true);
    setFields(tenantNameField);
  };

  const handleTenantNameClick = (row) => {
    let navigationLink = ROUTES.BOOKING_MANAGEMENT;
    if (row.isAssetSold) {
      navigationLink = ROUTES.SOLD_ASSET_MANAGEMENT; 
    }
    if (loginUserType === USER_TYPE.TENANT_USER) {
      const userRole = tenantUserRoles.tenants[row.id];
      dispatch(setUserRole(userRole));
    }
    dispatch(setselectedOrganization(row));
    navigate(navigationLink);
  };

  const handleAddModalSubmit = (values: any) => {
    setInputInitialValues(values);
    if (values.id) {
      // if (selectedOrganization?.['tenantName'] != values.tenantName) {
      //   dispatch(updateOrganization(values));
      // }
      dispatch(updateOrganization(values));
      // if (permissionData.value != values.allowToConnect) {
      //   dispatch(
      //     updateOrganizationPermission({
      //       orgId: values.id,
      //       role: ROLES.TENANT_OPERATOR,
      //       flag: values.allowToConnect
      //     })
      //   );
      // }
      closeDialog();
    } else {
      dispatch(saveOrganization(values ));
    }
  };

  const handleViewOrientation = (
    _event: MouseEvent<HTMLElement>,
    newValue: string | null
  ) => {
    if (newValue !== null) {
      setSelectedTab(newValue);
    }
  };

  useEffect(() => {
    if (isOrganizationListUpdated) {
      closeDialog();
      setIsOrganizationListUpdated(false);
      dispatch(getOrganizationData());
    }
  }, [isOrganizationListUpdated]);

  return (
    <>
      <Helmet>
        <title>BookingDLiteOrganazation</title>
      </Helmet>
      <PageTitleWrapper>
        <PageHeader
          heading={t('organization')}
          subHeading={t('organization-list')}
        >
          {loginUserRole === ROLES.SUPER_ADMIN ? (
            <Button
              size={'small'}
              sx={{ pt: 1, pb: 1 }}
              variant={'contained'}
              className="letter-spacing"
              startIcon={<AddTwoToneIcon fontSize="small" />}
              onClick={handleAddOrgButtonClick}
            >
              {t('add-organization')}
            </Button>
          ) : null}

          <TextField
            size={'small'}
            id="outlined-search"
            label={t('search-organization')}
            type="search"
            onChange={(e) => handleFilterOrganization(e.target.value)}
          />
          <ToggleButtonGroup
            value={selectedTab}
            exclusive
            onChange={handleViewOrientation}
            size={'small'}
          >
            <ToggleButton disableRipple value={LIST_VIEW.CARD_VIEW}>
              <GridViewSharpIcon />
            </ToggleButton>
            <ToggleButton disableRipple value={LIST_VIEW.TABLE_VIEW}>
              <TableRowsTwoToneIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </PageHeader>
      </PageTitleWrapper>

      <Container maxWidth="lg">
        <Grid
          container
          direction="row"
          justifyContent="center"
          alignItems="stretch"
          spacing={3}
        >
          <Grid item xs={12}>
            <OrganizationView
              inputLoginUserRole={loginUserRole}
              handleEditIconClick={handleEditIconClick}
              handleTenantNameClick={handleTenantNameClick}
              inputSelectedTab={selectedTab}
              inputOrganizationData={organizationList}
            />
          </Grid>
        </Grid>
        <SimpleDialog
          keepMounted
          fullWidth
          scroll="paper"
          model_title={
            <Typography sx={{ p: 3, pl: 2 }} variant="h4" fontWeight="bold">
              {isAdd ? t('add-organization') : t('edit-organization')}
            </Typography>
          }
          model_content={
            <Box>
              <CustomForm
                formFields={fields}
                editFieldValues={inputInitialValues}
                initialValues={inputInitialValues}
                submitBtnHandler={handleAddModalSubmit}
                cancelBtnHandler={handleAddModalCancel}
              />
            </Box>
          }
          open={open}
          onClose={handleClose}
        />
      </Container>
    </>
  );
};

export default Organization;
